import axios from 'axios';
import { getStorageSetting } from 'actions/storage';
import { updateConnectionStatus } from 'actions/common';
import { triggerSessionReset } from 'actions/auth';
import EnvConfig from 'env.config';

const {
  baseApiUrl,
  tokenName,
  apiEndpoints: {
    config: configUri,
    token: tokenUri,
    forms: formsUri,
    account: accountUri,
    customer: customerUri,
    details: detailsUri,
    formPdf: formPdfUri,
    viewPdf: viewPdfUri,
    bulkDelete: bulkDeleteUri,
    validation: validationUri,
    reporting: reportingUri,
    reportingXls: reportingXlsUri,
    auditTrail: auditTrailUri,
    allocationStmnts: allocationStmntsUri,
    w8imystructure: w8imystructureUri,
    miDashboard: miDashboardUri,
    miDashboardXls: miDashboardXlsUri,
    WhdCalculatedResults: WhdCalculatedResultsUri,
    workflow: workflowUri,
    withholding: withholdingUri,
    cicalert: cicalertUri,
    batches: batchesUri,
    legalEntities: legalEntitiesUri,
    activityPermissions: activitypermissionsUri,
    whdStatement: whdStatementUri,
    controllingPersons: controllingPersonsUri,
    tinMatchApi: tinMatchApiUri,
    staticData: staticDataUri
  }
} = EnvConfig;

// const HTTP_400_BADREQUEST = 400;
const HTTP_401_UNAUTHORIZED = 401;
// const HTTP_FORBIDDEN = 403;
// const HTTP_NOTFOUND = 404;
const HTTP_408_REQUESTTIMEOUT = 408;
const HTTP_429_TOOMANYREQUESTS = 429;
// const HTTP_501_NOTIMPLEMENTED = 501;
const HTTP_502_BADGATEWAY = 502;
const HTTP_503_SERVICEUNAVAILABLE = 503;
// const HTTP_GATEWAYTIMEOUT = 504;

const getDefaultConfig = () => {
  const selectedClient = getStorageSetting('selectedClient');
  let clientId = null;

  if (selectedClient) {
    clientId = selectedClient.key;
  }

  return ({
    headers: {
      Authorization: `Bearer ${ sessionStorage.getItem(tokenName) }`,
      'Content-Type': 'application/json',
      'TT-CLIENT': clientId
    },
    withCredentials: false
  });
};

const getFormDataConfig = () => {
  const config = Object.assign({}, getDefaultConfig());
  config.headers['Content-Type'] = 'multipart/form-data';
  return config;
};

const getDspConfig = (headers) => {
  const config = { headers, withCredentials: true };
  config.headers['Content-Type'] = 'application/json';
  return config;
};

// eslint-disable-next-line import/prefer-default-export
export class ApiService {
  static axios = axios.create({
    baseUrl: baseApiUrl
  });

  static signIn(creds) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ tokenUri }`,
        creds
      )
      .then((response) => {
        getDefaultConfig().Authorization = `Bearer ${ response.data.token }`;
        return response;
      });
  }

  static getAccessTokenName() {
    return `${ tokenName }`;
  }

  static getFileUploadUrl() {
    return `${ baseApiUrl }/file/upload`;
  }

  static getApiStatus() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }/system/status/json`,
        getDefaultConfig()
      );
  }

  static getDspConsole(url, headers) {
    const config = getDspConfig(headers);
    return ApiService.axios
      .post(
        `${ url }`,
        null,
        config
      );
  }

  static getSiteConfig() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/site`,
        getDefaultConfig()
      );
  }

  static getPaymentTypes() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/payment-types`,
        getDefaultConfig()
      );
  }

  static getJurisdictions() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/jurisdictions`,
        getDefaultConfig()
      );
  }

  static getSpecialRateProvisions(treatyCountry, hasPensionClaim) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/specialrateprovisions/${ treatyCountry }?hasPensionClaim=${ hasPensionClaim }`,
        getDefaultConfig()
      );
  }

  static getAuthState() {
    const config = Object.assign({}, getDefaultConfig(), { withCredentials: true });
    return ApiService.axios
      .get(
        `${ baseApiUrl }/auth/state`,
        config
      );
  }

  static getLogoutUrl() {
    return `${ baseApiUrl }/auth/logout`;
  }

  static getExternalLoginUrl() {
    return `${ baseApiUrl }/auth/external/login`;
  }

  static getUserConfig() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/user`,
        getDefaultConfig()
      );
  }

  static getActivityPermissions() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ activitypermissionsUri }`,
        getDefaultConfig()
      );
  }

  static addForm(formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/new`,
        formData,
        getDefaultConfig()
      );
  }

  static addWhdStatement(accountId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/add`,
        formData,
        getDefaultConfig()
      );
  }

  static addWhdForm(formType, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ WhdCalculatedResultsUri }/calc`,
        formData,
        getFormDataConfig()
      );
  }

  static createWhdStatement(formId, accountId, statementType) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/create`,
        {
          formId,
          type: statementType
        },
        getDefaultConfig()
      );
  }

  static addBeneficiaryForm(formType, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ formType }/pdf`,
        formData,
        getFormDataConfig()
      );
  }

  static deleteForm(formType, formId) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }${ formsUri }/${ formType && formType.toLowerCase() }/${ formId }`,
        getDefaultConfig()
      );
  }

  static deleteBatch(batchId) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/harddelete`,
        getDefaultConfig()
      );
  }

  static changeLegalEntity(batchId, legalEntityId) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/changele`,
        { batchId, legalEntityId },
        getDefaultConfig()
      );
  }

  static assignFormToUser(formsList, userId) {
    const formData = new FormData();
    formsList.map(item => formData.append('formsList', item));
    formData.append('userId', userId);

    return ApiService.axios
      .post(
        `${ baseApiUrl }/forms/assignforms`,
        formData,
        getFormDataConfig()
      );
  }

  static unassignFormToUser(forms) {
    const formData = new FormData();
    formData.append('formId', forms);

    return ApiService.axios
      .post(
        `${ baseApiUrl }/forms/unassignforms`,
        formData,
        getFormDataConfig()
      );
  }

  static deleteForms(formsIds) {
    const formData = new FormData();
    formsIds.map(item => formData.append('idsToDelete', item));
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }${ bulkDeleteUri }`,
        formData,
        getFormDataConfig()
      );
  }

  static getForms(pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected, formFamilies) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/page`,
        { pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected, formFamilies },
        getDefaultConfig()
      );
  }

  static getCustomers(pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumberSelected, accountNumber, customer, customerSelected) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/page`,
        { pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumberSelected, accountNumber, customer, customerSelected },
        getDefaultConfig()
      );
  }

  static getAccounts(pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/page`,
        { pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected },
        getDefaultConfig()
      );
  }

  static getLegalEntities(pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ legalEntitiesUri }/page`,
        { pageNumber, pageSize, sortByField, sortDirection, filterList, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected },
        getDefaultConfig()
      );
  }

  static getFormDetails(formId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }/${ formId }${ detailsUri }`,
        getDefaultConfig()
      );
  }

  static getFormStatement(formId, accountId, tierOwnersOnly) {
    const ownersOnlyStem = tierOwnersOnly ? '/1' : '';

    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }/${ formId }/whdstatement/${ accountId }${ ownersOnlyStem }`,
        getDefaultConfig()
      );
  }

  static getFormStatementLayerOwners(formId, accountId, tierAccountName) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ formId }/whdstatement/${ accountId }/owners`,
        { value: tierAccountName },
        getDefaultConfig()
      );
  }

  static getCustomerDetails(customerId, accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ customerUri }/${ customerId }${ detailsUri }/${ accountId }`,
        getDefaultConfig()
      );
  }

  static getCicAlertDetails(cicAlertId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ cicalertUri }/${ cicAlertId }${ detailsUri }`,
        getDefaultConfig()
      );
  }

  static getCompleteBatch(batchId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/complete`,
        getDefaultConfig()
      );
  }

  static getBatch(batchId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ batchesUri }/${ batchId }`,
        getDefaultConfig()
      );
  }

  static getBatches(pageNumber, pageSize, sortBy, sortDirection, filters) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/page`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filters },
        getDefaultConfig()
      );
  }

  static searchBatches(pageNumber, pageSize, sortBy, sortDirection, filters, searchQuery) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/page`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filters, searchQuery },
        getDefaultConfig()
      );
  }

  static getBatchFiles(batchId, pageNumber, pageSize, sortBy, sortDirection, filters) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/files`,
        { batchId, pageNumber, pageSize, sortByField: sortBy, sortDirection, filters },
        getDefaultConfig()
      );
  }

  static searchBatchFiles(batchId, pageNumber, pageSize, sortBy, sortDirection, filters, searchQuery) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/files`,
        { batchId, pageNumber, pageSize, sortByField: sortBy, sortDirection, filters, searchQuery },
        getDefaultConfig()
      );
  }

  static deleteBatchFile(batchId, fileId) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/files/${ fileId }`,
        getDefaultConfig()
      );
  }

  static getBatchForms(batchId, batchFileId, pageNumber, pageSize, sortBy, sortDirection, filters) {
    let batchFormsPath = 'forms';
    if (batchFileId && batchFileId > 0) {
      batchFormsPath = `files/${ batchFileId }`;
    }

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/${ batchFormsPath }`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filters },
        getDefaultConfig()
      );
  }

  static searchBatchForms(batchId, batchFileId, pageNumber, pageSize, sortBy, sortDirection, filters, searchQuery) {
    let batchFormsPath = 'forms';
    if (batchFileId && batchFileId > 0) {
      batchFormsPath = `files/${ batchFileId }`;
    }

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ batchesUri }/${ batchId }/${ batchFormsPath }`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filters, searchQuery },
        getDefaultConfig()
      );
  }

  static getBatchFilePdf(batchId, batchFileId, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ batchesUri }/${ batchId }/files/${ batchFileId }/download`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/pdf' });
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getWhdPaymentCalculations(accountNo) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ WhdCalculatedResultsUri }/search?query=${ encodeURIComponent(accountNo).replace(/'/g, '%27').replace(/"/g, '%22') }`,
        getDefaultConfig()
      );
  }

  static getFormById(formType, formId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }/${ formType }/${ formId }`,
        getDefaultConfig()
      );
  }

  static getValidationResults(pageNumber, pageSize, sortBy, sortDirection, filterList, activeTab) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ validationUri }/page`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filterList, activeTab },
        getDefaultConfig()
      );
  }

  static getPiiValidationCodes() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }/piivalidationcodes`,
        getDefaultConfig()
      );
  }

  static getFormPdf(file, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ formPdfUri }/${ file.id }?additional=${ file.isAdditional }`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: 'application/pdf' });
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          window.navigator.msSaveOrOpenBlob(blob, file.originalFileName);
          return null;
        }
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getPdfView(file, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }${ viewPdfUri }/${ file.id }?additional=${ file.isAdditional }`,
      method: 'get',
      transformResponse: (data) => {
        if (data.byteLength === 0) {
          return null;
        }
        const blob = new Blob([data], { type: 'application/pdf' });
        if (window.URL || window.webkitURL) {
          return (window.URL || window.webkitURL).createObjectURL(blob);
        }
        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          return window.navigator.msSaveOrOpenBlob(blob);
        }
        return null;
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getExcelReporting(config) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ reportingUri }${ reportingXlsUri }`,
        Object.assign({}, getDefaultConfig(), config)
      );
  }

  static getAuditTrailFile(formId, config) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ auditTrailUri }/${ formId }`,
        Object.assign({}, getDefaultConfig(), config)
      );
  }

  static getAllocationStatementFile(formId, config) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }${ allocationStmntsUri }/${ formId }`,
        Object.assign({}, getDefaultConfig(), config)
      );
  }

  static getW8ImyTreeStructure(formId, accountId, versionNumber) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ w8imystructureUri }/${ formId }/${ accountId }${ versionNumber ? '/versions/' : '' }${ versionNumber ?? '' }`,
        getDefaultConfig()
      );
  }

  static validateForm(formType, formId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ formType }/${ formId }`,
        formData,
        getDefaultConfig()
      );
  }

  static getMiDashboard(clientId, from, to, trendAnalysis) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ reportingUri }${ miDashboardUri }`,
        { clientId, from, to, trendAnalysis },
        getDefaultConfig()
      );
  }

  static getMiDashboardXls(clientId, from, to, trendAnalysis, config) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ reportingUri }${ miDashboardXlsUri }`,
        { clientId, from, to, trendAnalysis },
        Object.assign({}, getDefaultConfig(), config)
      );
  }

  static searchForms(pageNumber, pageSize, sortBy, sortDirection, filters, accountNumber, clientId, customer, legalEntity, searchQuery) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/search`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filters, accountNumber, clientId, customer, legalEntity, searchQuery },
        getDefaultConfig()
      );
  }

  static searchValidationResults(
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    filterList,
    searchQuery,
    dateFrom,
    dateTo,
    activeTab
  ) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ validationUri }/search`,
        { pageNumber, pageSize, sortByField: sortBy, sortDirection, filterList, searchQuery, dateFrom, dateTo, activeTab },
        getDefaultConfig()
      );
  }

  static cloneForm(formId, sourceCustomerId, sourceAccountId, customerId, accountId) {
    const formData = new FormData();

    formData.append('sourceCustomerId', sourceCustomerId);
    formData.append('sourceAccountId', sourceAccountId);
    formData.append('customerId', customerId);
    formData.append('accountId', accountId);

    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formsUri }/clone/${ formId }`,
        formData,
        getFormDataConfig()
      );
  }

  static moveForm(formId, sourceCustomerId, sourceAccountId, customerId, accountId) {
    const formData = new FormData();

    formData.append('sourceCustomerId', sourceCustomerId);
    formData.append('sourceAccountId', sourceAccountId);
    formData.append('customerId', customerId);
    formData.append('accountId', accountId);

    return ApiService.axios
      .put(
        `${ baseApiUrl }${ formsUri }/move/${ formId }`,
        formData,
        getFormDataConfig()
      );
  }

  static submitAnswers(data) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/new`,
        data,
        getDefaultConfig()
      );
  }

  static lookup(data) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }/autocomplete`,
        data,
        getDefaultConfig()
      );
  }

  static deleteTempUpload(guid) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }/file/upload/${ guid }`,
        getDefaultConfig()
      );
  }

  static deleteSupportingDoc(id) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }/customers/supporting-docs/${ id }`,
        getDefaultConfig()
      );
  }

  static getSupportingDoc(id, config) {
    return ApiService.axios({
      url: `${ baseApiUrl }/customers/supporting-docs/${ id }`,
      method: 'get',
      transformResponse: (data, headers) => {
        const blob = new Blob([data], { type: headers['content-type'] });

        if (window.navigator && window.navigator.msSaveOrOpenBlob && config) {
          window.navigator.msSaveOrOpenBlob(blob, this.getFileNameFromHeaders(headers));
          return null;
        }
        return (window.URL || window.webkitURL).createObjectURL(blob);
      },
      ...Object.assign({}, getDefaultConfig(), config)
    });
  }

  static getFormReviewState(formId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }/reviewstate/${ formId }`,
        getDefaultConfig()
      );
  }

  static saveFormReviewState(formId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/reviewstate/${ formId }`,
        formData,
        getFormDataConfig()
      );
  }

  static saveAccountReviewComments(formId, accountId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/reviewcomments/${ formId }/account/${ accountId }`,
        formData,
        getFormDataConfig()
      );
  }

  static saveAccountComments(accountId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }/accounts/${ accountId }/comment`,
        formData,
        getFormDataConfig()
      );
  }

  static getUserQueue(workflow, pageNumber, pageSize, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected, formFamilies, sortByField, sortDirection, filterList) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ workflowUri }/user/current`,
        { workflow, pageNumber, pageSize, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected, formFamilies, sortByField, sortDirection, filterList },
        getDefaultConfig()
      );
  }

  static getWorkflowQueues(workflow, pageNumber, pageSize) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ workflowUri }/user/queues`,
        { workflow, pageNumber, pageSize },
        getDefaultConfig()
      );
  }

  static getWorkflowQueue(workflow, pageNumber, pageSize, stage, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected, formFamilies, sortByField, sortDirection, filterList) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ workflowUri }/queue/${ stage }`,
        { workflow, pageNumber, pageSize, searchQuery, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected, formFamilies, sortByField, sortDirection, filterList },
        getDefaultConfig()
      );
  }

  static takeQueueItem(workflow, id) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ workflowUri }/${ workflow }/take/${ id }`,
        getDefaultConfig()
      );
  }

  static saveDraftValidationOverrides(id, accountId, data) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ validationUri }/overrides/${ id }/draft/${ accountId }`,
        data,
        getDefaultConfig()
      );
  }

  static acceptValidationOverrides(id, accountId, data) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ validationUri }/overrides/${ id }/current/${ accountId }`,
        data,
        getDefaultConfig()
      );
  }

  static acceptFormOverride(id, data) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ validationUri }/overrides/${ id }/form`,
        data,
        getDefaultConfig()
      );
  }

  static getCustomerData(customerId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ customerUri }/${ customerId }/getwithdata`,
        getDefaultConfig()
      );
  }

  static getBasicCustomer(customerId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ customerUri }/${ customerId }`,
        getDefaultConfig()
      );
  }

  static getBasicAccount(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }/accounts/${ accountId }`,
        getDefaultConfig()
      );
  }

  static linkCustomerAccount(customerId, accounts) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/accounts/link`,
        accounts,
        getDefaultConfig()
      );
  }

  static unLinkCustomerAccount(customerId, accountIds) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/accounts/unlink`,
        accountIds,
        getDefaultConfig()
      );
  }

  static updateCustomerId(customerId, newCustomerId) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/updateId`,
        newCustomerId,
        getFormDataConfig()
      );
  }

  static updateCustomer(customerId, emailAddress) {
    const formData = new FormData();

    formData.append('emailAddress', emailAddress ?? '');

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/update`,
        formData,
        getFormDataConfig()
      );
  }

  static getCustomerLegalEntities(customerId) {
    const uri = `${ customerId || -1 }`;
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ customerUri }/${ uri }${ legalEntitiesUri }`,
        getDefaultConfig()
      );
  }

  static editCustomerLegalEntityLinks(customerIdList, newLegalEntityId, batchId) {
    const formData = new FormData();
    customerIdList.map(item => formData.append('customerIdList', item));
    formData.append('newLegalEntityId', newLegalEntityId);
    formData.append('batchId', batchId);

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }${ legalEntitiesUri }/update`,
        formData,
        getFormDataConfig()
      );
  }

  static addCustomerAccount(data) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/add`,
        data,
        getDefaultConfig()
      );
  }

  static updateFormReference(formId, newFormRef) {
    const formData = new FormData();
    formData.append('newFormRef', newFormRef);

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ formId }/updateformref`,
        formData,
        getFormDataConfig()
      );
  }

  static editFormAssociations(formId, changes) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ formsUri }/${ formId }/reassign`,
        changes,
        getDefaultConfig()
      );
  }

  static getAccountData(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ accountUri }/${ accountId }/getwithdata`,
        getDefaultConfig()
      );
  }

  static unLinkAccountForm(accountId, formIds) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/${ accountId }/forms/unlink`,
        formIds,
        getDefaultConfig()
      );
  }

  static linkAccountForm(accountId, formId) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/${ accountId }/forms/link`,
        formId,
        getDefaultConfig()
      );
  }

  static updateDualFormStatus(accountId, status) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/${ accountId }/updatedualformstatus`,
        status.toString(),
        getDefaultConfig()
      );
  }

  static updateAccountNumber(accountId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/${ accountId }/updateaccountnumber`,
        formData,
        getFormDataConfig()
      );
  }

  static updateAccountName(accountId, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/${ accountId }/update`,
        formData,
        getFormDataConfig()
      );
  }

  static getDetectedWithholdingRates(formId, customerId, accountId) {
    const normalisedAccountId = accountId != null ? accountId : '0';
    if (customerId) {
      return this.getCustomerWithholdingRates(customerId, normalisedAccountId);
    }

    if (formId) {
      return this.getFormWithholdingRates(formId, normalisedAccountId);
    }

    return this.getAccountWithholdingRates(accountId);
  }

  static getCustomerAccountWithholdingRates(customerId, accountId) {
    const normalisedAccountId = accountId || 0;
    if (normalisedAccountId === 0) {
      return this.getCustomerWithholdingRates(customerId, normalisedAccountId);
    }

    return this.getAccountWithholdingRates(accountId);
  }

  static getFormWithholdingRates(formId, accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ withholdingUri }/rates/${ formId }/${ accountId }`,
        getDefaultConfig()
      );
  }

  static getAccountWithholdingRates(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ withholdingUri }/rates/account/${ accountId }`,
        getDefaultConfig()
      );
  }

  static getCustomerWithholdingRates(customerId, accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ withholdingUri }/rates/customer/${ customerId }/${ accountId }`,
        getDefaultConfig()
      );
  }

  static getAccountWithholdingStatements(accountIds) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/withholdingstatements`,
        accountIds,
        getDefaultConfig()
      );
  }

  static getAccountWithholdingStatement(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }`,
        getDefaultConfig()
      );
  }

  static getAccountWithholdingStatementVersion(accountId, versionNumber) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/versions/${ versionNumber }`,
        getDefaultConfig()
      );
  }

  static updateWithholdingStatementImyParent(accountId, changeDetails) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/changeparent`,
        changeDetails,
        getDefaultConfig()
      );
  }

  static getParentImyForms(customerId, accountId) {
    const recordType = customerId > 0 ? 'customer' : 'account';
    const recordId = customerId > 0 ? customerId : accountId || 0;
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ formsUri }/imyforms/${ recordType }/${ recordId }`,
        getDefaultConfig()
      );
  }

  static addBeneficialOwner(accountId, parentGuid, addImyLayer) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/addrow`,
        {
          parentGuid,
          addImyLayer
        },
        getDefaultConfig()
      );
  }

  static updateStatement(accountId, updates) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/update`,
        updates,
        getDefaultConfig()
      );
  }

  static updateStatementRow(accountId, rowItem) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/updaterow`,
        rowItem,
        getDefaultConfig()
      );
  }

  static deleteStatementRow(accountId, rowToDelete) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/deleterow`,
        rowToDelete,
        getDefaultConfig()
      );
  }

  static publishStatement(accountId, versionWindow) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/publish`,
        {
          effectiveFromDate: versionWindow.effectiveFromDate,
          effectiveToDate: versionWindow.effectiveToDate
        },
        getDefaultConfig()
      );
  }

  static updateStatementEffectiveDates(accountId, versionWindow) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/editversion/${ versionWindow.version }`,
        {
          effectiveFromDate: versionWindow.effectiveFromDate,
          effectiveToDate: versionWindow.effectiveToDate
        },
        getDefaultConfig()
      );
  }

  static getStatementVersions(accountId, pageNumber) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ whdStatementUri }/${ accountId }/versions`,
        { pageNumber },
        getDefaultConfig()
      );
  }

  static updatePassword(updateData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }/updateUserPassword`,
        updateData,
        getFormDataConfig()
      );
  }

  static getWorkflowCicAlerts(
    workflowStage,
    pageNumber,
    pageSize,
    sortByField,
    sortDirection,
    filterList,
    raisedWithin,
    legalEntity,
    legalEntitySelected,
    accountNumber,
    accountNumberSelected,
    customer,
    customerSelected
  ) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ cicalertUri }/queue/${ workflowStage }`,
        { pageNumber, pageSize, sortByField, sortDirection, filterList, raisedWithin, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected },
        getDefaultConfig()
      );
  }

  static getUserCicAlerts(
    pageNumber,
    pageSize,
    sortByField,
    sortDirection,
    filterList,
    raisedWithin,
    legalEntity,
    legalEntitySelected,
    accountNumber,
    accountNumberSelected,
    customer,
    customerSelected
  ) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ cicalertUri }/user/current`,
        { pageNumber, pageSize, sortByField, sortDirection, filterList, raisedWithin, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected },
        getDefaultConfig()
      );
  }

  static getCicAlerts(
    resolved,
    pageNumber,
    pageSize,
    sortByField,
    sortDirection,
    filterList,
    raisedWithin,
    legalEntity,
    legalEntitySelected,
    accountNumber,
    accountNumberSelected,
    customer,
    customerSelected
  ) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ cicalertUri }/list`,
        { resolved, pageNumber, pageSize, sortByField, sortDirection, filterList, raisedWithin, legalEntity, legalEntitySelected, accountNumber, accountNumberSelected, customer, customerSelected },
        getDefaultConfig()
      );
  }

  static getCicAlert(id) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ cicalertUri }/${ id }`,
        getDefaultConfig()
      );
  }

  static takeCicAlert(id) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ cicalertUri }/take/${ id }`,
        getDefaultConfig()
      );
  }

  static assignCicToUser(cicsList, userID) {
    const formData = new FormData();
    cicsList.map(item => formData.append('cicsList', item));
    formData.append('userId', userID);

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ cicalertUri }/assigntouser`,
        formData,
        getFormDataConfig()
      );
  }

  static saveCicReviewState(id, formData) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ cicalertUri }/reviewstate/${ id }`,
        formData,
        getFormDataConfig()
      );
  }

  static getCicAlertCounts() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ cicalertUri }/alert-counts`,
        getDefaultConfig()
      );
  }

  static switchClientRefreshToken() {
    return axios.get(
      `${ baseApiUrl }/switchClientRefreshToken`,
      getDefaultConfig()
    ).then((response) => {
      if (typeof response.data.code !== 'undefined' && response.data.code === '401') {
        window.location.reload();
      } else if (response.data.token != null) {
        getDefaultConfig().headers.Authorization = `Bearer ${ response.data.token }`;

        const originalToken = sessionStorage.getItem(EnvConfig.firstTokenName);
        if (!originalToken) {
          sessionStorage.setItem(EnvConfig.firstTokenName, sessionStorage.getItem(EnvConfig.tokenName));
        }

        sessionStorage.setItem(EnvConfig.tokenName, response.data.token);
      }
    }).catch((error) => {
      console.error(error);
    });
  }

  static getFileNameFromHeaders(headers) {
    const disposition = headers && headers['content-disposition'];

    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        const filename = matches[1].replace(/['"]/g, '');
        return (filename || 'form.pdf');
      }
    }
    return 'form.pdf';
  }

  static getAllControllingPersons(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ controllingPersonsUri }/${ accountId }/controllingpersons`,
        getDefaultConfig()
      );
  }

  static addControllingPerson(accountId, entityFormId, cpFormId, personType) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ controllingPersonsUri }/${ accountId }/controllingpersons`,
        { entityFormId, cpFormId, personType },
        getDefaultConfig()
      );
  }

  static expireControllingPerson(controllingPersonId, accountId, effectiveDate) {
    return ApiService.axios
      .put(
        `${ baseApiUrl }${ controllingPersonsUri }/${ accountId }/controllingpersons/${ controllingPersonId }/expire`,
        { effectiveDate },
        getDefaultConfig()
      );
  }

  static removeControllingPerson(controllingPersonId, accountId) {
    return ApiService.axios
      .delete(
        `${ baseApiUrl }${ controllingPersonsUri }/${ accountId }/controllingpersons/${ controllingPersonId }/remove`,
        getDefaultConfig()
      );
  }

  static linkAccountToImyStructure(accountId, accountIdToLinkTo) {
    return ApiService.axios
      .post(
        `${ baseApiUrl }${ accountUri }/${ accountId }/linkImyStructure`,
        accountIdToLinkTo,
        getDefaultConfig()
      );
  }

  static getLinkOperationStatus(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ accountUri }/${ accountId }/linkoperationstatus`,
        getDefaultConfig()
      );
  }

  static getLinkOperationStatuses(accountIds) {
    let idQueryString = '';
    if (accountIds && accountIds.length > 0) {
      idQueryString = `ids=${ accountIds.join('&ids=') }`;
    }
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ accountUri }/linkoperationstatus?${ idQueryString }`,
        getDefaultConfig()
      );
  }

  static getAccountLinkedImyStructure(accountId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ accountUri }/${ accountId }/imystructure`,
        getDefaultConfig()
      );
  }

  static checkTin(formId) {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ tinMatchApiUri }/checktin/${ formId }`,
        getDefaultConfig()
      );
  }

  static getIgaSubClassificationCombos() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ configUri }/igasubclassifications`,
        getDefaultConfig()
      );
  }

  static getStockMarkets() {
    return ApiService.axios
      .get(
        `${ baseApiUrl }${ staticDataUri }/getstockmarkets`,
        getDefaultConfig()
      );
  }

  static changePrimaryAddress(customerId, newAddressId) {
    const formData = new FormData();
    formData.append('newPrimaryAddressId', newAddressId);

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/addresses/changeprimary`,
        formData,
        getFormDataConfig()
      );
  }

  static changePrimaryJotr(customerId, newJotrId) {
    const formData = new FormData();
    formData.append('newPrimaryTaxResidenceId', newJotrId);

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/jotrs/changeprimary`,
        formData,
        getFormDataConfig()
      );
  }

  static changeWhdJotr(customerId, newJotrId) {
    const formData = new FormData();
    formData.append('newWhdTaxResidenceId', newJotrId);

    return ApiService.axios
      .post(
        `${ baseApiUrl }${ customerUri }/${ customerId }/jotrs/changewhd`,
        formData,
        getFormDataConfig()
      );
  }

  /* Retry Code */
  static queuedPromises = [];

  static isProcessingQueue = false;

  static startQueuedRequestRetryTimeout = 0;

  static globalRetryCount = 0;

  static insertQueuedPromise(promise) {
    if (promise != null) {
      if (typeof promise.priority === 'undefined') {
        promise.priority = 99;
      }

      const idx = ApiService.queuedPromises.findIndex(x => !x.priority || x.priority < promise.priority);
      ApiService.queuedPromises.splice(idx, 0, promise);
    }
  }

  static addQueuedPromise(promise, error) {
    if (promise != null) {
      ApiService.queuedPromises.push(promise);
    }
  }

  static retryPromise(promise) {
    if (!promise) {
      return Promise.reject(new Error('Empty promise'));
    }

    ApiService.globalRetryCount += 1;
    return Promise.resolve(promise());
  }

  static retryRequest(request) {
    if (!request) {
      return Promise.reject(new Error('Empty promise'));
    }

    const requestConfig = { ...request, retries: (request.retries || 0) + 1 };
    ApiService.globalRetryCount += 1;
    return Promise.resolve(ApiService.axios.request(requestConfig));
  }

  static delayRetryRequest(ms, requestConfig) {
    return new Promise((resolve) => { setTimeout(resolve, ms); }).then(() => ApiService.retryRequest(requestConfig));
  }

  static processQueuedPromises(bypassCheck) {
    if (!bypassCheck && ApiService.isProcessingQueue) {
      return;
    }

    ApiService.isProcessingQueue = true;
    ApiService.startQueuedRequestRetryTimeout = 0;
    clearTimeout(ApiService.startQueuedRequestRetryTimeout);

    if (ApiService.queuedPromises && ApiService.queuedPromises.length > 0) {
      const request = ApiService.queuedPromises.shift();
      ApiService.retryPromise(
        request
      ).then(() => {
        // Should only hit here if successful?
        ApiService.startQueuedRequestRetryTimeout = 0;
        clearTimeout(ApiService.startQueuedRequestRetryTimeout);
        ApiService.isProcessingQueue = false;
        if (ApiService.globalRetryCount < 2) {
          ApiService.processQueuedPromises(false);
        }
      }).catch((error) => {
        ApiService.isProcessingQueue = false;
        Promise.reject(error);
      });
    } else {
      ApiService.isProcessingQueue = false;
    }
  }
}

ApiService.axios.interceptors.response.use(
  (response) => {
    ApiService.processQueuedPromises(false);
    ApiService.globalRetryCount = 0;
    updateConnectionStatus(true, 0);
    return response;
  },
  (error) => {
    if (error.response) {
      if (error.response.status === HTTP_401_UNAUTHORIZED) {
        const url = error.response.config && error.response.config.url;
        if (url !== `${ baseApiUrl }${ tokenUri }` && url !== `${ baseApiUrl }/auth/state`) {
          localStorage.setItem('taina.reset', Date.now());
          triggerSessionReset();
          return new Promise(() => {});
        }
      }

      if ([HTTP_408_REQUESTTIMEOUT, HTTP_429_TOOMANYREQUESTS, HTTP_502_BADGATEWAY, HTTP_503_SERVICEUNAVAILABLE].indexOf(error.response.status) > -1 && error.response.config) {
        if (error.response.config.retries >= 1) {
          error = { ...error, retries: error.response.config.retries };
          if (error.response.status === HTTP_429_TOOMANYREQUESTS || error.response.config.retries === 1) {
            return ApiService.delayRetryRequest(3 * 1000, error.response.config);
          }
        } else {
          return ApiService.retryRequest(error.response.config);
        }
      }
    } else if (error.message === 'Network Error') {
      updateConnectionStatus(false, ApiService.globalRetryCount);
      if (error.config && ApiService.globalRetryCount >= 1) {
        error = { ...error, retries: ApiService.globalRetryCount };
        if (error.config.retries === 1) {
          return ApiService.delayRetryRequest(3 * 1000, error.config);
        }
      } else {
        return ApiService.retryRequest(error.config);
      }
    }

    return Promise.reject(error);
  }
);

function refreshToken() {
  if (getDefaultConfig().headers.Authorization.length > 15) {
    axios.get(
      `${ baseApiUrl }/refreshToken`,
      getDefaultConfig()
    ).then((response) => {
      if (typeof response.data.code !== 'undefined' && response.data.code === HTTP_401_UNAUTHORIZED) {
        localStorage.setItem('taina.reset', Date.now());
        triggerSessionReset();
      } else if (response.data.token != null) {
        getDefaultConfig().headers.Authorization = `Bearer ${ response.data.token }`;

        const originalToken = sessionStorage.getItem(EnvConfig.firstTokenName);
        if (!originalToken) {
          sessionStorage.setItem(EnvConfig.firstTokenName, sessionStorage.getItem(EnvConfig.tokenName));
        }

        sessionStorage.setItem(EnvConfig.tokenName, response.data.token);
      }
    }).catch((error) => {
      console.error(error);
    });
  }
  setTimeout(refreshToken, 300000);
}

setTimeout(refreshToken, 300000);
